<script setup lang="ts">
// @ts-expect-error JS file
import store from '@/store'
import BKSvgIcon from '@/components/AppShared/BKSvgIcon.vue'
import { EventDB } from '@/enitites/event/model/types'
import { useRouter, useRoute } from 'vue-router/composables'
import { computed } from 'vue'
import IconEdit from '@/shared/icons/IconEdit.vue'
import ToggleLike from '@/features/toggle-like/ui/ToggleLike.vue'
import IconLike from '@/shared/icons/IconLike.vue'
import { EVENT_STAR_CONFIG } from '../constants'

interface Props {
  event?: EventDB
}

const props = withDefaults(defineProps<Props>(), {
  event: undefined,
})

const router = useRouter()
const route = useRoute()

const getUserDB = computed(() => store.getters.getUserDB)

const handleClickBottomButton = () => {
  if (getUserDB.value) {
    if (router.currentRoute.query.tab !== 'feedback') {
      router.push({
        name: 'Event',
        params: { id: `${props.event?.id}` },
        query: { tab: 'feedback' },
      })
    }
  } else {
    router.push({
      name: 'Signup',
      query: { redirect: route.path },
    })
  }
}
</script>

<template>
  <div
    v-if="event.isFinished"
    class="feedback-wrapper"
    :style="{
      display: 'flex',
    }"
  >
    <div class="feedback-container">
      <template>
        <ToggleLike
          v-if="event.permissions.canLike"
          class="feedback-item"
          :is-liked="event.isLiked"
          :total-likes="event.countLikes"
          :event-id="event.id"
          @update:is-liked="(newState) => (event.isLiked = newState)"
          @update:total-likes="(newTotalLikes) => (event.countLikes = newTotalLikes)"
        />
        <div
          v-else
          class="feedback-item"
        >
          Likes
          <IconLike />
          {{ event.countLikes }}
        </div>
      </template>

      <div
        v-if="event.permissions.canLeaveFeedback && event.permissions.canLike"
        class="feedback-divider"
      />

      <button
        v-if="event.permissions.canLeaveFeedback"
        class="feedback-item"
        @click="handleClickBottomButton"
      >
        <template v-if="!event.feedback">
          <span>Provide feedback</span>
          <BKSvgIcon
            class="arrow-icon"
            color="#fff"
            height="24px"
            name="arrow-with-stem"
            width="24px"
          />
        </template>
        <template v-else>
          <div class="feedback">
            <div class="feedback-header">Edit feedback <IconEdit /></div>
            <v-rating
              v-bind="EVENT_STAR_CONFIG"
              :value="event.feedback.averageRating"
              color="white"
              background-color="white"
              readonly
            />
            <div class="feedback-footer">Your avg rating</div>
          </div>
        </template>
      </button>
    </div>
  </div>
</template>

<style scoped lang="scss">
@import '@/assets/style/mixins';

.feedback-wrapper {
  position: relative;
  display: block;
}

.feedback-container {
  background: rgb(0 0 0 / 30%);
  border: 1px solid rgb(255 255 255 / 30%);
  border-radius: 8px;
  box-shadow: 0 1px 4px rgb(0 0 0 / 20%);
}

.feedback-item {
  @include font-h5;

  position: relative;
  display: flex;
  gap: 8px;
  align-items: center;
  justify-content: flex-start;
  width: 100%;
  padding: 20px;
  color: white;
  background: none;
  border: none;
  transition: background-color 0.2s ease;

  &:hover {
    background: rgb(255 255 255 / 5%);
  }
}

.feedback-divider {
  width: 100%;
  height: 1px;
  background: rgb(255 255 255 / 30%);
}

.feedback {
  display: flex;
  flex-direction: column;
  gap: 4px;
  align-items: start;
  justify-content: start;

  .feedback-header {
    @include font-body1;

    display: flex;
    gap: 8px;
    align-items: center;
  }

  .feedback-footer {
    @include font-caption1;

    color: #fffc;
    text-align: left;
  }
}

.icon {
  position: absolute;
  top: 10px;
  right: 10px;
}

.star {
  gap: 5px;

  :deep(button) {
    padding: 0;
  }
}

.arrow-icon {
  margin-left: 4px;
  transform: rotate(180deg);
}
</style>
